/**
 * The Room board's pieces (core roomboard.ts; docs/ROOM_STATE.md, STANDARDS S15): the glance line for Today, one room's
 * ordered steps, and the shop tablet's read-only board. Each row is one idea: a picture, what to do (12 words at most),
 * why it's on the list (Info: "Required by law · Food Code 3-501.16"), about how long, and one action. The board never
 * checks anything off itself: every action opens the screen that already does it.
 */
import { Link } from "react-router-dom";
import type { BoardRoom, RoomBoard, RoomStep } from "@logwave/core";
import { Icon } from "./Icons.tsx";
import { NagButton } from "./Nag.tsx";
import { ErrorBox, InfoLine, LinkButton, Pill, Section, Skeleton } from "./ui.tsx";
import { MINUTE_CHOICES, MOMENT_TONE, STEP_ICON, actionPath, minutesLabel } from "../lib/roomBoard.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSession } from "../session.tsx";

/** Today's one line: "Rooms: Restroom 2 · Line 1 · Dish 0 ›". Quiet when it can't load (Today never breaks for it). */
export function RoomsLine({ version = 0 }: { version?: number }) {
  const api = useApi();
  const s = useSession();
  const b = useLoad(() => api.roomBoard(s.org.id, s.location.id), [api, s.org.id, s.location.id, version]);
  if (!b.data) return null;
  const grave = b.data.counts.grave > 0;
  return (
    <Link to="/rooms" className={`card rb-line${grave ? " edge edge-stop" : ""}`} aria-label={`Rooms: ${b.data.line}. ${b.data.momentLine}`}>
      <Icon name={grave ? "alert" : "today"} size={24} />
      <span className="rb-line-text"><span className="kicker">Rooms</span><strong>{b.data.line}</strong></span>
      <Pill tone={grave ? "stop" : MOMENT_TONE[b.data.moment]} icon={grave ? "alert" : "clock"}>{grave ? "Something is grave" : b.data.momentLine}</Pill>
      <Icon name="chevron" size={20} />
    </Link>
  );
}

/** One step: picture, what, why, how long, one action (primary on the first row only: one primary per screen). */
export function RoomStepRow({ step, primary, actions = true }: { step: RoomStep; primary?: boolean; actions?: boolean }) {
  const to = actionPath(step);
  const variant = primary || step.gravity === "grave" ? "primary" : "secondary";
  return (
    <li className="rb-step" data-gravity={step.gravity} data-basis={step.basis} data-id={step.id}>
      <span className={`rb-icon rb-icon-${step.gravity}`} aria-hidden="true"><Icon name={STEP_ICON[step.icon]} size={28} /></span>
      <div className="rb-body">
        <p className="rb-what">{step.what}</p>
        {step.lateLine && <Pill tone={step.gravity === "grave" ? "stop" : step.lateSince && step.gravity === "required" ? "stop" : "warn"} icon="clock">{step.lateLine}</Pill>}
        <InfoLine label={step.basis === "sense" ? "Suggested" : "Why"} value={step.basis === "sense" ? step.basisReason ?? step.why : step.basisLine} icon={step.basis === "sense" ? "info" : "prove"} />
        <p className="caption">{minutesLabel(step)} · {step.estWhy}</p>
      </div>
      {actions && (
        <div className="rb-action">
          {to ? <LinkButton to={to} variant={variant} icon="chevron" iconEnd>{step.action.label}</LinkButton>
            : <NagButton small={false} item={{ ref: `report:${step.action.target.id}`, title: step.what, urgency: step.action.urgent ? "urgent" : "normal" }} />}
        </div>
      )}
    </li>
  );
}

/** A room: its status, the steps now, and what's folded (after the rush, for a lull, past the budget). Never dropped. */
export function RoomSteps({ room, actions = true }: { room: BoardRoom; actions?: boolean }) {
  return (
    <section className="stack-sm" aria-label={`${room.name}: ${room.count} now`}>
      <p className="row"><Pill tone={room.grave ? "stop" : room.right ? "go" : "warn"} icon={room.grave ? "alert" : room.right ? "check" : "clock"}>{room.status}</Pill>{room.minutes > 0 && <span className="caption">About {room.minutes} min in all</span>}</p>
      {room.steps.length > 0 ? (
        <ul className="rb-steps" aria-label={`${room.name} steps`}>
          {room.steps.map((st, i) => <RoomStepRow key={st.id} step={st} primary={i === 0} actions={actions} />)}
        </ul>
      ) : <p className="muted">Nothing to do here now.</p>}
      {room.later.length > 0 && (
        <details className="card card-flat">
          <summary className="disclose">{room.laterLine}</summary>
          <ul className="rb-steps" aria-label={`${room.name}: later`}>{room.later.map((st) => <RoomStepRow key={st.id} step={st} actions={actions} />)}</ul>
        </details>
      )}
    </section>
  );
}

/** The room picker: one button per room with its count; grave rooms say so in words, not only color. */
export function RoomPicker({ board, room, onRoom }: { board: RoomBoard; room?: string; onRoom: (name: string) => void }) {
  return (
    <div className="btn-row rb-rooms" role="group" aria-label="Rooms">
      {board.rooms.map((r) => (
        <button key={r.name} type="button" className="chip rb-room" aria-pressed={r.name === room} onClick={() => onRoom(r.name)}>
          {r.grave && <Icon name="alert" size={16} label="grave" />}{r.name} <strong>{r.count}</strong>
        </button>
      ))}
    </div>
  );
}

/** "I have N minutes": a budget (value first; grave and law steps always stay). */
export function MinutesPicker({ minutes, onMinutes }: { minutes?: number; onMinutes: (m?: number) => void }) {
  return (
    <div className="btn-row" role="group" aria-label="I have this many minutes">
      <span className="field-label">I have</span>
      {MINUTE_CHOICES.map((m) => <button key={m} type="button" className="chip" aria-pressed={minutes === m} onClick={() => onMinutes(minutes === m ? undefined : m)}>{m} min</button>)}
      <button type="button" className="chip" aria-pressed={minutes === undefined} onClick={() => onMinutes(undefined)}>No limit</button>
    </div>
  );
}

/** Close and after: everything still open, law first, so the next person picks it up. */
export function Handoff({ board }: { board: RoomBoard }) {
  if (!board.handoff.length) return null;
  return (
    <Section kicker="Hand off" title={board.handoffLine}>
      <ul className="rb-steps" aria-label="Still open">
        {board.handoff.map((st) => (
          <li key={st.id} className="rb-step rb-step-compact" data-gravity={st.gravity}>
            <span className={`rb-icon rb-icon-${st.gravity}`} aria-hidden="true"><Icon name={STEP_ICON[st.icon]} size={22} /></span>
            <div className="rb-body"><p className="rb-what">{st.room}: {st.what}</p><p className="caption">{st.basisLine}{st.lateLine ? ` · ${st.lateLine}` : ""}</p></div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** The shop tablet: the glance line, then the board to read (acting needs a person: their phone or Quick log). */
export function KioskRooms({ orgId, locationId }: { orgId: string; locationId: string }) {
  const api = useApi();
  const b = useLoad(() => api.roomBoard(orgId, locationId), [api, orgId, locationId]);
  if (!b.data) return b.error ? <ErrorBox error={`We couldn't load the rooms. ${b.error}`} onRetry={b.reload} /> : <Skeleton rows={1} label="Loading rooms" />;
  return (
    <details className="card rb-kiosk">
      <summary className="disclose"><strong>Rooms: {b.data.line}</strong> <Pill tone={b.data.counts.grave ? "stop" : MOMENT_TONE[b.data.moment]} icon="clock">{b.data.momentLine}</Pill></summary>
      <div className="stack">
        {b.data.rooms.map((r) => <div key={r.name}><h2 className="rb-room-title">{r.name}</h2><RoomSteps room={r} actions={false} /></div>)}
        <p className="caption">Done one? Log it on your phone, or with Quick log below.</p>
      </div>
    </details>
  );
}
